import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { createEssentials } from "@wix/fe-essentials-standalone";
import { RootEssentialsProvider } from "@wix/fe-essentials-standalone/react";
import "@wix/design-system/styles.global.css";
import "@wix/picasso-theme/themes/picasso-light-theme.css";
import "@wix/picasso-theme/themes/picasso-dark-theme.css";
import { WixDesignSystemProvider } from "@wix/design-system";
import type { Route } from "./+types/root";
import { QueryClient, QueryClientProvider } from "react-query";
import { ClientOnly } from "remix-utils/client-only";
import { MainApp } from "./mainApp";
import { MiddleScreenLoader } from "./landingPage/components/Common/MiddleScreenLoader";
// import { web } from "@wix/wix-common-aspects";
import { ThemeProvider } from "@wix/picasso-common";
import { SHOW_APP_THEME_TOGGLE_EXPERIMENT } from "./consts";

export async function loader({ context }: Route.LoaderArgs) {
  // const supportedLanguages = [
  //   "cs", "da", "de", "en", "es", "fr", "he", "id", "it", "ja",
  //   "ko", "nl", "no", "pl", "pt", "ru", "sv", "th", "tr", "uk",
  //   "vi", "zh",
  // ];
  // const languageCode = supportedLanguages.includes(
  //   context.wixRequest.aspects.get(web.Language),
  // )
  //   ? context.wixRequest.aspects.get(web.Language)
  //   : "en";

  const experimentBag = await context.bootstrapContext.petri
    .client(context.wixRequest.aspects)
    .conductAllInScope("picasso");

  const isForceHebrewEnabled =
    experimentBag["specs.picasso.forceHebrew"] === "true";
  const isDontForceEnglishEnabled =
    experimentBag["specs.picasso.dontForceEnglish"] === "true";

  let languageCode = "en";
  if (isForceHebrewEnabled) {
    languageCode = "he";
  } else if (!isDontForceEnglishEnabled) {
    languageCode = "en";
  }

  const messages = await import(
    `../locales/messages_${languageCode}.json`
  ).then((module) => module.default);

  return { experimentBag, languageCode, messages };
}

export function Layout(props: { children: React.ReactNode }) {
  const { languageCode } = useLoaderData<typeof loader>();

  return (
    <html
      lang={languageCode}
      dir={languageCode === "he" ? "rtl" : "ltr"}
      style={{ height: "100%" }}
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          name="google-site-verification"
          content="UB6oJeVXxC-pYSmQODwectCX2P05N42eCCZFQO_Ndxg"
        />
        <Meta />
        <Links />
        <link
          rel="stylesheet"
          href="//static.parastorage.com/unpkg/@wix/wix-fonts@1.9.3/madeforDisplay.css"
        />
        <script
          src="https://static.parastorage.com/unpkg/@sentry/browser@5.30.0/build/bundle.min.js"
          crossOrigin="anonymous"
        ></script>
        <title>
          Vibe Code a Website | From Text to Web Pages in Minutes | Wix
        </title>
        <meta
          name="description"
          content="Just Describe your vision and watch it build a functioning website. Vibe code a website from simple prompts - no coding, no stress, just your style in minutes."
        />
      </head>
      <body style={{ margin: "0" }}>
        {props.children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App(props: Route.ComponentProps) {
  const essentials = createEssentials({
    i18n: { messages: props.loaderData.messages },
    experiments: { bag: props.loaderData.experimentBag },
    environment: {
      artifactId: "picasso-landing-page",
      language: props.loaderData.languageCode,
      version: "1.0.0",
    },
  });

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 0,
      },
    },
  });

  const isShowAppThemeToggleEnabled = essentials.experiments.enabled(
    SHOW_APP_THEME_TOGGLE_EXPERIMENT,
  );

  return (
    <QueryClientProvider client={client}>
      <ThemeProvider enabled={isShowAppThemeToggleEnabled}>
        <WixDesignSystemProvider>
          <RootEssentialsProvider essentials={essentials}>
            <ClientOnly fallback={<MiddleScreenLoader />}>
              {() => (
                <MainApp>
                  <Outlet />
                </MainApp>
              )}
            </ClientOnly>
          </RootEssentialsProvider>
        </WixDesignSystemProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
